import React, { useEffect, useMemo, useState } from 'react';

type CallStatus = { success?: boolean; message?: string; [k: string]: any } | null;
type FlowResults = {
  originPath?: string;
  donor?: { name?: string; email?: string; amount?: string; donationType?: string };
  payment?: CallStatus;
  addDonation?: CallStatus;
  email?: (CallStatus & { receiptUrl?: string }) | null;
} | null;

const Success: React.FC = () => {
  const [results, setResults] = useState<FlowResults>(null);
  // Use brand-specific external assets (provided)
  const DEFAULT_LOGO = '';
  const RMS_LOGO = 'https://www.radhamadhavsociety.org/assets/images/about/i-logo-2.png';
  const HYC_LOGO = 'https://harmonyyouthcenter.org/Content/images/footer.png';
  const [brandLogoSrc, setBrandLogoSrc] = useState<string>('');
  const [logoError, setLogoError] = useState<boolean>(false);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = 'Donation Success';
    }
    if (typeof window !== 'undefined') {
      try {
        const raw = window.sessionStorage.getItem('donationFlowResults');
        if (raw) setResults(JSON.parse(raw));
      } catch {}
    }
  }, []);

  const allSucceeded = !!(
    results?.payment?.success &&
    results?.addDonation?.success &&
    ((typeof (results as any)?.email === 'string' && !!(results as any)?.email) || (results as any)?.email?.success)
  );
  const statusStyle = (ok?: boolean): React.CSSProperties => (
    ok
      ? { background: '#d1e7dd', color: '#0f5132', border: '1px solid #badbcc', padding: '12px 16px', borderRadius: 8 }
      : { background: '#f8d7da', color: '#842029', border: '1px solid #f5c2c7', padding: '12px 16px', borderRadius: 8 }
  );

  const goBack = () => {
    const origin = (results as any)?.originPath;
    if (typeof window !== 'undefined') {
      window.location.href = typeof origin === 'string' && origin ? origin : '/';
    }
  };

  const receiptUrl = useMemo(() => {
    const e: any = results?.email || {};
    if (typeof e === 'string') return e;
    return (
      e?.receiptUrl ||
      e?.ReceiptUrl ||
      e?.receiptURL ||
      e?.Response ||
      e?.response ||
      e?.data?.receiptUrl ||
      e?.data?.ReceiptUrl ||
      e?.data?.receiptURL ||
      e?.data?.Response ||
      e?.data?.response ||
      ''
    );
  }, [results]);

  const paymentKnown = typeof results?.payment?.success === 'boolean';
  const paymentOk = results?.payment?.success === true;
  const headerText = paymentKnown ? (paymentOk ? 'Payment Successful' : 'Payment Failed') : 'Submission Results';

  const emailOk = useMemo(() => {
    const e: any = (results as any)?.email;
    if (typeof e === 'string') return !!e; // raw URL implies success
    return !!e?.success;
  }, [results]);

  const emailMessage = useMemo(() => {
    const e: any = (results as any)?.email;
    if (typeof e === 'string') return 'A confirmation email was sent.';
    return e?.message || (emailOk ? 'A confirmation email was sent.' : 'Failed to send confirmation email.');
  }, [results, emailOk]);

  // Pick brand based on origin path
  useEffect(() => {
    const origin = (results as any)?.originPath?.toLowerCase?.() || '';
    setLogoError(false);
    if (/^\/hyc(\/|$)/.test(origin)) {
      setBrandLogoSrc(HYC_LOGO);
    } else if (/^\/rms(\/|$)/.test(origin)) {
      setBrandLogoSrc(RMS_LOGO);
    } else {
      setBrandLogoSrc(DEFAULT_LOGO);
    }
  }, [results]);

  const brandAlt = useMemo(() => {
    const origin = (results as any)?.originPath?.toLowerCase?.() || '';
    if (/^\/hyc(\/|$)/.test(origin)) return 'Harmony Youth Center';
    if (/^\/rms(\/|$)/.test(origin)) return 'Radha Madhav Society';
    return 'Yugal Kunj';
  }, [results]);

  // Set document title with brand context
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const title = brandAlt ? `${brandAlt} Donation - Success` : 'Donation Success';
      document.title = title;
    }
  }, [brandAlt]);

  return (
    <div style={{
      minHeight: '70vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: '24px'
    }}>
      {!logoError && brandLogoSrc ? (
        <img
          src={brandLogoSrc}
          alt={brandAlt}
          style={{ width: 140, height: 'auto', marginBottom: 12 }}
          onError={() => setLogoError(true)}
        />
      ) : null}
      <h1 style={{
        fontSize: '2rem',
        margin: 0,
        fontWeight: 700,
        textAlign: 'center',
        color: paymentKnown ? (paymentOk ? '#0f5132' : '#842029') : undefined
      }}>
        {headerText}
      </h1>

      {allSucceeded ? (
        <p style={{ marginTop: 8, color: '#155724', textAlign: 'center', maxWidth: 720, background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 6, padding: '8px 12px' }}>
          All steps completed successfully. Thank you for your donation!
        </p>
      ) : (
        <p style={{ marginTop: 8, color: '#555', textAlign: 'center', maxWidth: 720 }}>
          Below are the results of your submission.
        </p>
      )}

      {!results && (
        <div style={{ marginTop: 16, ...statusStyle(false), maxWidth: 900 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No Results Found</div>
          <div>We couldn't find any recent submission results. Please complete the donation form first.</div>
          <div style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-outline-secondary" onClick={goBack}>Go Back</button>
          </div>
        </div>
      )}

      {results?.donor?.name && (
        <p style={{ marginTop: 4, color: '#555', textAlign: 'center', maxWidth: 720 }}>
          Donor: <strong>{results.donor.name}</strong>
          {results?.donor?.email ? ` — Email: ${results.donor.email}` : ''}
          {results?.donor?.donationType ? ` — Type: ${results.donor.donationType}` : ''}
          {results?.donor?.amount ? ` — Amount: $${results.donor.amount}` : ''}
        </p>
      )}

      <div style={{ width: '100%', maxWidth: 900, marginTop: 24 }}>
        {/* Payment Result */}
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: '1.1rem', marginBottom: 8 }}>Payment</h3>
          <div style={statusStyle(results?.payment?.success)}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{results?.payment?.success ? 'Success' : 'Failed'}</div>
            <div>{results?.payment?.message || (results?.payment?.success ? 'Payment processed successfully.' : 'Payment could not be processed.')}</div>
          </div>
        </div>

        {/* Add Donation Result */}
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: '1.1rem', marginBottom: 8 }}>Add Donation Record</h3>
          <div style={statusStyle(results?.addDonation?.success)}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{results?.addDonation?.success ? 'Success' : 'Failed'}</div>
            <div>{results?.addDonation?.message || (results?.addDonation?.success ? 'Donation recorded successfully.' : 'Unable to record donation.')}</div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: '1.1rem', marginBottom: 8 }}>Email Receipt</h3>
          <div style={statusStyle(emailOk)}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{emailOk ? 'Success' : 'Failed'}</div>
            <div>{emailMessage}</div>
             {receiptUrl ? (
               <div style={{ marginTop: 12 }}>
                 <a href={receiptUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary" style={{ textDecoration: 'none' }}>
                   Download Attachment
                 </a>
               </div>
             ) : null}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button type="button" className="btn btn-outline-secondary" onClick={goBack}>Go Back</button>
      </div>
    </div>
    </div>
  );
};

export default Success;
